import { Theme } from '@emotion/react';
import {
  Box,
  IconButton,
  Pagination,
  PaginationItem,
  Stack,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

interface CustomPaginationProps {
  handlePagination: (skip: number) => Promise<void>;
  limit: number;
  sx?: SxProps<Theme>;
  totalRecord: number;
}

export function CustomPagination({
  sx,
  totalRecord,
  limit,
  handlePagination,
}: CustomPaginationProps) {
  const totalPages = useMemo(
    () => Math.ceil(totalRecord / limit),
    [totalRecord, limit]
  );

  const [page, setPage] = useState(1);
  const [inputPage, setInputPage] = useState<number>(1);

  useEffect(() => {
    const skip = (page - 1) * limit;
    void handlePagination(skip);
  }, [page, limit, handlePagination]);

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setInputPage(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*$/.test(val)) {
      const num = Number(val);
      if (num <= totalPages) {
        setInputPage(num);
      }
    }
  };

  const handleInputBlur = () => {
    const value = Math.min(totalPages, Math.max(1, Number(inputPage)));
    setPage(value);
    setInputPage(value);
  };

  const CustomArrow = ({ direction }: { direction: 'left' | 'right' }) => (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: 'white',
        borderRadius: '50%',
        display: 'flex',
        height: 50,
        justifyContent: 'center',
        width: 50,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 40 40"
        width="24"
        height="24"
      >
        <path
          d={
            direction === 'left'
              ? 'M5 19.9827L40 0V40L5 19.9827Z'
              : 'M35 19.9827L0 0V40L35 19.9827Z'
          }
          fill="#3287e1"
        />
      </svg>
    </Box>
  );

  const goToPrev = () => {
    if (page > 1) {
      const prev = page - 1;
      setPage(prev);
      setInputPage(prev);
    }
  };

  const goToNext = () => {
    if (page < totalPages) {
      const next = page + 1;
      setPage(next);
      setInputPage(next);
    }
  };
  if (totalRecord > 0) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          flexDirection: {
            sm: 'row',
            xs: 'column',
          },
          width: {
            sm: 'auto',
            xs: '100%',
          },
          ...(sx || {}),
        }}
      >
        <IconButton
          onClick={goToPrev}
          sx={{
            display: {
              sm: 'inline-flex',
              xs: 'none',
            },
            p: 0,
          }}
        >
          <CustomArrow direction="left" />
        </IconButton>

        <Pagination
          count={totalPages}
          page={page}
          onChange={handleChange}
          variant="outlined"
          shape="rounded"
          siblingCount={1}
          boundaryCount={1}
          hideNextButton
          hidePrevButton
          renderItem={(item) => (
            <PaginationItem
              {...item}
              sx={{
                '& .MuiPaginationItem-icon': {
                  backgroundColor: '#1976d2',
                  borderRadius: '50%',
                  color: '#fff',
                  padding: '4px',
                },
                '&.Mui-selected': {
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                  backgroundColor: '#1976d2',
                  color: '#fff',
                },
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
                border: 'none',
                borderRadius: 0,
              }}
            />
          )}
        />

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          mt={{ sm: 0, xs: 2 }}
        >
          <IconButton
            onClick={goToPrev}
            sx={{
              display: {
                sm: 'none',
                xs: 'inline-flex',
              },
              p: 0,
            }}
          >
            <CustomArrow direction="left" />
          </IconButton>

          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              value={inputPage}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const validPage = Math.min(
                    totalPages,
                    Math.max(1, inputPage)
                  );
                  setPage(validPage);
                  setInputPage(validPage);
                }
              }}
              size="small"
              inputProps={{
                inputMode: 'numeric',
                max: totalPages,
                min: 1,
                style: {
                  background: 'white',
                  padding: '4px 16px',
                  textAlign: 'right',
                  width: 40,
                },
              }}
            />
            <Typography variant="body2">/ {totalPages}</Typography>
          </Stack>

          <IconButton onClick={goToNext} sx={{ p: 0 }}>
            <CustomArrow direction="right" />
          </IconButton>
        </Stack>
      </Stack>
    );
  }
  return <></>;
}
